/**
 * Runtime API Configuration for China/Global endpoint switching.
 *
 * Detects whether the user is in mainland China (via timezone + locale heuristic)
 * and routes all API traffic through the Cloudflare Worker proxy when needed.
 *
 * The CF Worker proxy (api-reedle-cn.dimp.studio) is globally accessible,
 * so CN users always use it with negligible overhead (~1-5ms).
 */

const ENDPOINTS = {
  global: {
    supabaseUrl: 'https://fhgxapmrciwlhsffdeyj.supabase.co',
    renderJsUrl: 'https://reedle-server-js.onrender.com',
    renderPyUrl: 'https://reedle-server-py-eu.onrender.com',
    googleFontsBase: 'https://fonts.googleapis.com',
  },
  cn: {
    supabaseUrl: 'https://api-reedle-cn.dimp.studio/supabase',
    renderJsUrl: 'https://api-reedle-cn.dimp.studio/render-js',
    renderPyUrl: 'https://api-reedle-cn.dimp.studio/render-py',
    googleFontsBase: 'https://fonts.loli.net',
  },
};

const STORAGE_KEY = 'reedle-api-region';
const FAILOVER_KEY = 'reedle-failover-attempted';

/**
 * Front-door origin (P4-a, BOARD #42, ADR-005 amendment d).
 * LIVE since P6 cutover 2026-10-27 (A: door → rime, healthz 200; render-exit
 * log phase 3). Global render traffic goes through the door; CN keeps its own
 * worker proxy (GFW) — the door never overrides CN (see getters below).
 */
const FRONT_DOOR_URL = 'https://api-reedle.dimp.studio';

/**
 * Detect region using timezone + locale heuristic.
 * - Asia/Shanghai + zh* language → 'cn'
 * - Everything else → 'global'
 *
 * NOT cached — always runs the heuristic fresh.
 * @returns {'global' | 'cn'}
 */
export function detectRegion() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const lang = navigator.language || '';
    if (tz === 'Asia/Shanghai' && lang.startsWith('zh')) {
      return 'cn';
    }
  } catch {
    // Intl not available — default to global
  }
  return 'global';
}

/**
 * Get the current API region. Reads from cache first, then detects.
 * @returns {'global' | 'cn'}
 */
export function getRegion() {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached === 'global' || cached === 'cn') return cached;
  } catch {
    // localStorage unavailable
  }
  const region = detectRegion();
  try {
    localStorage.setItem(STORAGE_KEY, region);
  } catch {
    // ignore
  }
  return region;
}

/** @returns {boolean} */
export function isCnRegion() {
  return getRegion() === 'cn';
}

/**
 * Get the raw stored region preference (what the user has manually set).
 * Does not fall back to auto-detection.
 * @returns {'global' | 'cn' | 'auto'}
 */
export function getStoredRegion() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'global' || stored === 'cn') return stored;
  } catch {
    // localStorage unavailable
  }
  return 'auto';
}

/**
 * Manually set the API region and reload the page to apply.
 * @param {'global' | 'cn' | 'auto'} value
 *   'auto' = remove override, revert to heuristic detection on next load
 */
export function setRegion(value) {
  try {
    if (value === 'auto') {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    // ignore
  }
  window.location.reload();
}

// Resolve once at module load — all consumers read from this object
const region = getRegion();

/** The resolved API endpoint configuration for the current region. */
export const apiConfig = {
  ...ENDPOINTS[region],
  region,
  // P4-a: when the door is live, GLOBAL render traffic goes through it.
  // CN keeps its own worker proxy (GFW) — the door never overrides CN.
  // Rollback = set FRONT_DOOR_URL back to null (direct URLs stay below).
  frontDoorUrl: FRONT_DOOR_URL,
  get renderJsUrl() {
    if (FRONT_DOOR_URL && region !== 'cn') return `${FRONT_DOOR_URL}/render-js`;
    return ENDPOINTS[region].renderJsUrl;
  },
  get renderPyUrl() {
    if (FRONT_DOOR_URL && region !== 'cn') return `${FRONT_DOOR_URL}/render-py`;
    return ENDPOINTS[region].renderPyUrl;
  },
};

/**
 * Rewrite a Supabase Storage URL for the current region.
 * In CN mode, replaces the direct supabase.co domain with the CF Worker proxy.
 * In global mode, returns the URL unchanged.
 *
 * Handles URLs stored in article markdown by the cache-images edge function.
 */
export function rewriteSupabaseStorageUrl(url) {
  if (!url || region !== 'cn') return url;
  return url.replace(
    'https://fhgxapmrciwlhsffdeyj.supabase.co',
    ENDPOINTS.cn.supabaseUrl,
  );
}

/**
 * Attempt failover by switching to the other region and reloading.
 * Protected against infinite loops via sessionStorage flag.
 */
export function attemptFailover() {
  try {
    if (sessionStorage.getItem(FAILOVER_KEY)) {
      console.warn('[apiConfig] Failover already attempted this session, skipping');
      return;
    }
    sessionStorage.setItem(FAILOVER_KEY, '1');
  } catch {
    // sessionStorage unavailable — don't failover
    return;
  }

  const current = getRegion();
  const next = current === 'cn' ? 'global' : 'cn';
  console.log(`[apiConfig] Failing over from '${current}' to '${next}'`);

  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // can't persist — reload won't help
    return;
  }

  window.location.reload();
}
